import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, Checkbox, FormControlLabel, Grid, MenuItem,
  Paper, Select, FormControl, InputLabel, FormHelperText,
  Snackbar, Alert, Stack, Step, StepLabel, Stepper,
  TextField, Typography, Divider,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  SmokingRooms as SmokingRoomsIcon,
  LocalBar as LocalBarIcon,
  LocalHospital as LocalHospitalIcon,
  Medication as MedicationIcon,
  FitnessCenter as FitnessCenterIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import ToggleSection from './ToggleSection';
import GenericCheckboxGroup from './GenericCheckbox';
import TensionArterial from './TensionArterial';
import { useEntrevistaForm } from '../hooks/useEntrevistaForm';

const COLORS = {
  primary: '#0d6aee',
  primaryDark: '#1d4ed8',
  textMain: '#0f172a',
  textMuted: '#475569',
  labelMuted: '#64748b',
  border: '#dbe7f3',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    '& fieldset': { borderColor: '#cbd5e1' },
    '&:hover fieldset': { borderColor: COLORS.primary },
    '&.Mui-focused fieldset': { borderColor: COLORS.primary, borderWidth: 2 },
  },
};

const sectionPaperSx = {
  p: { xs: 2, md: 3 },
  mb: 3,
  borderRadius: 3,
  border: `1px solid ${COLORS.border}`,
  background: '#f8fbff',
  boxShadow: '0 4px 12px rgba(15,23,42,0.04)',
};

const STEP_LABELS_FULL = ['Identificación', 'Hábitos', 'Antecedentes', 'Medicación', 'Actividades'];
const STEP_LABELS_FINAL = ['Identificación', 'Datos finales'];

const SectionTitle = ({ icon, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
    <Box sx={{ color: COLORS.primary, display: 'flex' }}>{icon}</Box>
    <Typography variant="h6" sx={{ color: COLORS.textMain, fontWeight: 700 }}>
      {children}
    </Typography>
  </Box>
);

// ── Steps ─────────────────────────────────────────────────────────────────────

const StepIdentificacion = ({ pacienteId, tipoEntrevista, setTipoEntrevista, stepErrors }) => (
  <Paper elevation={0} sx={sectionPaperSx}>
    <SectionTitle icon={<AssignmentIcon />}>Datos de la entrevista</SectionTitle>
    <Divider sx={{ mb: 3, borderColor: COLORS.border }} />
    <Grid container spacing={3}>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label="ID del paciente"
          value={pacienteId}
          disabled
          sx={fieldSx}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth required error={!!stepErrors.tipoEntrevista}>
          <InputLabel>Tipo de entrevista</InputLabel>
          <Select
            value={tipoEntrevista}
            onChange={(e) => setTipoEntrevista(e.target.value)}
            label="Tipo de entrevista"
            sx={{
              borderRadius: '10px',
              backgroundColor: '#ffffff',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.primary },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.primary, borderWidth: 2 },
            }}
          >
            <MenuItem value="Inicial">Inicial (Septiembre)</MenuItem>
            <MenuItem value="Final">Final (Junio)</MenuItem>
          </Select>
          {stepErrors.tipoEntrevista && (
            <FormHelperText>{stepErrors.tipoEntrevista}</FormHelperText>
          )}
        </FormControl>
      </Grid>
    </Grid>
    {tipoEntrevista && (
      <Box sx={{ mt: 3 }}>
        <Chip
          label={`Entrevista ${tipoEntrevista}`}
          sx={{ backgroundColor: '#dbeafe', color: '#1e3a8a', fontWeight: 600 }}
        />
      </Box>
    )}
  </Paper>
);

const StepHabitos = ({ fumador, setFumador, alcohol, setAlcohol }) => (
  <Paper elevation={0} sx={sectionPaperSx}>
    <Grid container spacing={4}>
      <Grid item xs={12} sm={6}>
        <SectionTitle icon={<SmokingRoomsIcon />}>Tabaquismo</SectionTitle>
        <Divider sx={{ mb: 3, borderColor: COLORS.border }} />
        <FormControl variant="outlined" fullWidth>
          <Typography variant="subtitle1" gutterBottom>¿Es fumador/a?</Typography>
          <Select fullWidth value={fumador} onChange={(e) => setFumador(e.target.value)}>
            <MenuItem value="Si">Si</MenuItem>
            <MenuItem value="No">No</MenuItem>
            <MenuItem value="Fue fumador">Fue fumador</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <SectionTitle icon={<LocalBarIcon />}>Consumo de alcohol</SectionTitle>
        <Divider sx={{ mb: 3, borderColor: COLORS.border }} />
        <FormControl variant="outlined" fullWidth>
          <Typography variant="subtitle1" gutterBottom>¿Consume o consumía alcohol?</Typography>
          <Select fullWidth value={alcohol} onChange={(e) => setAlcohol(e.target.value)}>
            <MenuItem value="Si">Si</MenuItem>
            <MenuItem value="No">No</MenuItem>
            <MenuItem value="Consumía alcohol">Consumía alcohol</MenuItem>
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  </Paper>
);

const StepAntecedentes = ({
  caidas12Meses, setCaidas12Meses, numCaidas, setNumCaidas,
  ayudaLocomocion, setAyudaLocomocion,
  protesisArticulares, setProtesisArticulares, protesis, setProtesis,
  vertigos, setVertigos, mareosAndar, setMareosAndar,
  cardiovascular, setCardiovascular, cardioOptions, setCardioOptions,
  respiratorio, setRespiratorio, respOptions, setRespOptions,
  genitoUrinario, setGenitoUrinario, genitoOptions, setGenitoOptions,
  endocrino, setEndocrino, endocrinoOptions, setEndocrinoOptions,
  digestivo, setDigestivo, digestOptions, setDigestOptions,
  musculoEsqueletico, setMusculoEsqueletico, musculoOptions, setMusculoOptions,
  nervioso, setNervioso, nervOptions, setNervOptions,
  sentidos, setSentidos, sentidosOptions, setSentidosOptions,
}) => (
  <Stack spacing={3}>
    {/* Movilidad y caídas */}
    <Paper elevation={0} sx={sectionPaperSx}>
      <SectionTitle icon={<LocalHospitalIcon />}>Movilidad y caídas</SectionTitle>
      <Divider sx={{ mb: 3, borderColor: COLORS.border }} />
      <ToggleSection
        label="¿Ha sufrido caídas en los últimos 12 meses?"
        value={caidas12Meses}
        onChange={(e) => setCaidas12Meses(e.target.checked)}
      >
        <TextField
          label="Número de caídas"
          type="number"
          value={numCaidas}
          onChange={(e) => setNumCaidas(e.target.value)}
          sx={{ ...fieldSx, width: 200, mt: 1 }}
        />
      </ToggleSection>
      <FormControlLabel
        control={<Checkbox checked={ayudaLocomocion} onChange={(e) => setAyudaLocomocion(e.target.checked)} sx={{ color: COLORS.primary, '&.Mui-checked': { color: COLORS.primary } }} />}
        label="¿Utiliza algún tipo de ayuda para la locomoción?"
      />
      <Box sx={{ mt: 2 }}>
        <ToggleSection
          label="¿Utiliza prótesis articulares?"
          value={protesisArticulares}
          onChange={(e) => setProtesisArticulares(e.target.checked)}
        >
          <GenericCheckboxGroup
            title="Prótesis articulares"
            options={protesis}
            onOptionChange={(key, value) => setProtesis((p) => ({ ...p, [key]: value }))}
          />
        </ToggleSection>
      </Box>
      <Box sx={{ mt: 1 }}>
        <FormControlLabel
          control={<Checkbox checked={vertigos} onChange={(e) => setVertigos(e.target.checked)} sx={{ color: COLORS.primary, '&.Mui-checked': { color: COLORS.primary } }} />}
          label="¿Sufre de vértigos?"
        />
        <FormControlLabel
          control={<Checkbox checked={mareosAndar} onChange={(e) => setMareosAndar(e.target.checked)} sx={{ color: COLORS.primary, '&.Mui-checked': { color: COLORS.primary } }} />}
          label="¿Sufre de mareos al andar?"
        />
      </Box>
    </Paper>

    {/* Sistemas clínicos */}
    <Paper elevation={0} sx={sectionPaperSx}>
      <SectionTitle icon={<LocalHospitalIcon />}>Antecedentes por sistemas</SectionTitle>
      <Divider sx={{ mb: 3, borderColor: COLORS.border }} />
      <Stack spacing={1}>
        <ToggleSection label="Cardiovascular" value={cardiovascular} onChange={(e) => setCardiovascular(e.target.checked)}>
          <GenericCheckboxGroup title="Enfermedades cardiovasculares" options={cardioOptions} onOptionChange={(k, v) => setCardioOptions((p) => ({ ...p, [k]: v }))} />
        </ToggleSection>
        <ToggleSection label="Respiratorio" value={respiratorio} onChange={(e) => setRespiratorio(e.target.checked)}>
          <GenericCheckboxGroup title="Enfermedades respiratorias" options={respOptions} onOptionChange={(k, v) => setRespOptions((p) => ({ ...p, [k]: v }))} />
        </ToggleSection>
        <ToggleSection label="Genito-urinario" value={genitoUrinario} onChange={(e) => setGenitoUrinario(e.target.checked)}>
          <GenericCheckboxGroup title="Enfermedades genito-urinarias" options={genitoOptions} onOptionChange={(k, v) => setGenitoOptions((p) => ({ ...p, [k]: v }))} />
        </ToggleSection>
        <ToggleSection label="Endocrino" value={endocrino} onChange={(e) => setEndocrino(e.target.checked)}>
          <GenericCheckboxGroup title="Enfermedades endocrinas" options={endocrinoOptions} onOptionChange={(k, v) => setEndocrinoOptions((p) => ({ ...p, [k]: v }))} />
        </ToggleSection>
        <ToggleSection label="Digestivo" value={digestivo} onChange={(e) => setDigestivo(e.target.checked)}>
          <GenericCheckboxGroup title="Enfermedades digestivas" options={digestOptions} onOptionChange={(k, v) => setDigestOptions((p) => ({ ...p, [k]: v }))} />
        </ToggleSection>
        <ToggleSection label="Musculoesquelético" value={musculoEsqueletico} onChange={(e) => setMusculoEsqueletico(e.target.checked)}>
          <GenericCheckboxGroup title="Áreas afectadas" options={musculoOptions} onOptionChange={(k, v) => setMusculoOptions((p) => ({ ...p, [k]: v }))} />
        </ToggleSection>
        <ToggleSection label="Sistema nervioso" value={nervioso} onChange={(e) => setNervioso(e.target.checked)}>
          <GenericCheckboxGroup title="Enfermedades del sistema nervioso" options={nervOptions} onOptionChange={(k, v) => setNervOptions((p) => ({ ...p, [k]: v }))} />
        </ToggleSection>
        <ToggleSection label="Sentidos" value={sentidos} onChange={(e) => setSentidos(e.target.checked)}>
          <GenericCheckboxGroup title="Áreas afectadas" options={sentidosOptions} onOptionChange={(k, v) => setSentidosOptions((p) => ({ ...p, [k]: v }))} />
        </ToggleSection>
      </Stack>
    </Paper>
  </Stack>
);

const StepMedicacion = ({
  medicacion, setMedicacion, medOptions, setMedOptions,
  numAnalgesicos, setNumAnalgesicos,
  tensionPMM, setTensionPMM, tensionPAS, setTensionPAS, tensionPAD, setTensionPAD,
  datosInteres, setDatosInteres,
}) => (
  <Stack spacing={3}>
    <Paper elevation={0} sx={sectionPaperSx}>
      <SectionTitle icon={<MedicationIcon />}>Medicación</SectionTitle>
      <Divider sx={{ mb: 3, borderColor: COLORS.border }} />
      <ToggleSection
        label="¿Está tomando alguna medicación?"
        value={medicacion}
        onChange={(e) => setMedicacion(e.target.checked)}
      >
        <GenericCheckboxGroup
          title="Medicamentos"
          options={medOptions}
          onOptionChange={(k, v) => setMedOptions((p) => ({ ...p, [k]: v }))}
        />
      </ToggleSection>
      <Box sx={{ mt: 2, maxWidth: 240 }}>
        <TextField
          fullWidth
          label="Número de analgésicos"
          type="number"
          value={numAnalgesicos}
          onChange={(e) => setNumAnalgesicos(e.target.value)}
          sx={fieldSx}
        />
      </Box>
    </Paper>

    <Paper elevation={0} sx={sectionPaperSx}>
      <SectionTitle icon={<MedicationIcon />}>Tensión arterial</SectionTitle>
      <Divider sx={{ mb: 3, borderColor: COLORS.border }} />
      <TensionArterial
        tensionPMM={tensionPMM} setTensionPMM={setTensionPMM}
        tensionPAS={tensionPAS} setTensionPAS={setTensionPAS}
        tensionPAD={tensionPAD} setTensionPAD={setTensionPAD}
      />
    </Paper>

    <Paper elevation={0} sx={sectionPaperSx}>
      <SectionTitle icon={<MedicationIcon />}>Observaciones</SectionTitle>
      <Divider sx={{ mb: 3, borderColor: COLORS.border }} />
      <TextField
        fullWidth
        label="Datos de interés"
        multiline
        rows={4}
        value={datosInteres}
        onChange={(e) => setDatosInteres(e.target.value)}
        sx={fieldSx}
      />
    </Paper>
  </Stack>
);

const StepActividades = ({
  actividades, setActividades,
  tipoEntrevista,
  caidasFinales, setCaidasFinales,
  numPastillas, setNumPastillas,
  numAnalgesicosFinal, setNumAnalgesicosFinal,
  cambioMedicacionFinal, setCambioMedicacionFinal,
  consultaMedico, setConsultaMedico,
  ingresosHospital, setIngresosHospital,
  datosInteresFinal, setDatosInteresFinal,
}) => {
  const actividadesSeleccionadas = Object.keys(actividades).filter((k) => actividades[k]);

  return (
    <Stack spacing={3}>
      <Paper elevation={0} sx={sectionPaperSx}>
        <SectionTitle icon={<FitnessCenterIcon />}>Actividades en las que participa</SectionTitle>
        <Divider sx={{ mb: 3, borderColor: COLORS.border }} />
        <Grid container spacing={1}>
          {Object.keys(actividades).map((actividad) => (
            <Grid item xs={12} sm={6} md={4} key={actividad}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={actividades[actividad]}
                    onChange={(e) => setActividades((p) => ({ ...p, [actividad]: e.target.checked }))}
                    sx={{ color: COLORS.primary, '&.Mui-checked': { color: COLORS.primary } }}
                  />
                }
                label={actividad}
              />
            </Grid>
          ))}
        </Grid>
        {actividadesSeleccionadas.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {actividadesSeleccionadas.map((a) => (
              <Chip key={a} label={a} size="small" sx={{ backgroundColor: '#dbeafe', color: '#1e3a8a', fontWeight: 600 }} />
            ))}
          </Box>
        )}
      </Paper>

      {tipoEntrevista === 'Final' && (
        <Paper elevation={0} sx={{ ...sectionPaperSx, border: `1px solid #fcd34d`, background: '#fffbeb' }}>
          <SectionTitle icon={<AssignmentIcon />}>Entrevista final</SectionTitle>
          <Divider sx={{ mb: 3, borderColor: '#fcd34d' }} />
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Caídas durante la participación"
                type="number"
                value={caidasFinales}
                onChange={(e) => setCaidasFinales(e.target.value)}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Número de pastillas"
                type="number"
                value={numPastillas}
                onChange={(e) => setNumPastillas(e.target.value)}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Número de analgésicos"
                type="number"
                value={numAnalgesicosFinal}
                onChange={(e) => setNumAnalgesicosFinal(e.target.value)}
                sx={fieldSx}
              />
            </Grid>
          </Grid>
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={<Checkbox checked={cambioMedicacionFinal} onChange={(e) => setCambioMedicacionFinal(e.target.checked)} sx={{ color: COLORS.primary, '&.Mui-checked': { color: COLORS.primary } }} />}
                label="Cambio de medicación"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={<Checkbox checked={consultaMedico} onChange={(e) => setConsultaMedico(e.target.checked)} sx={{ color: COLORS.primary, '&.Mui-checked': { color: COLORS.primary } }} />}
                label="Consulta médico (empeoramiento)"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={<Checkbox checked={ingresosHospital} onChange={(e) => setIngresosHospital(e.target.checked)} sx={{ color: COLORS.primary, '&.Mui-checked': { color: COLORS.primary } }} />}
                label="Ingresos en hospital"
              />
            </Grid>
          </Grid>
          <TextField
            fullWidth
            label="Detalles adicionales"
            multiline
            rows={3}
            value={datosInteresFinal}
            onChange={(e) => setDatosInteresFinal(e.target.value)}
            sx={fieldSx}
          />
        </Paper>
      )}
    </Stack>
  );
};

// ── Datos Finales (simplified Final interview step) ───────────────────────────

const StepDatosFinales = ({
  tensionPMM, setTensionPMM, tensionPAS, setTensionPAS, tensionPAD, setTensionPAD,
  caidasFinales, setCaidasFinales,
  numPastillas, setNumPastillas,
  numAnalgesicosFinal, setNumAnalgesicosFinal,
  cambioMedicacionFinal, setCambioMedicacionFinal,
  consultaMedico, setConsultaMedico,
  ingresosHospital, setIngresosHospital,
  datosInteresFinal, setDatosInteresFinal,
  expandarHistorialCompleto,
}) => (
  <Stack spacing={3}>
    {/* Prompt to expand */}
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: '1px solid #bfdbfe',
        background: '#eff6ff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2,
      }}
    >
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e3a8a' }}>
          Entrevista final simplificada
        </Typography>
        <Typography variant="body2" sx={{ color: '#3b82f6' }}>
          Se muestran solo los datos esenciales. Puede ampliar para registrar el historial completo.
        </Typography>
      </Box>
      <Button
        variant="outlined"
        size="small"
        endIcon={<ExpandMoreIcon />}
        onClick={expandarHistorialCompleto}
        sx={{ borderRadius: 2.5, borderColor: '#3b82f6', color: '#1e3a8a', whiteSpace: 'nowrap' }}
      >
        Añadir historial completo
      </Button>
    </Paper>

    {/* Tensión arterial */}
    <Paper elevation={0} sx={sectionPaperSx}>
      <SectionTitle icon={<MedicationIcon />}>Tensión arterial</SectionTitle>
      <Divider sx={{ mb: 3, borderColor: COLORS.border }} />
      <TensionArterial
        tensionPMM={tensionPMM} setTensionPMM={setTensionPMM}
        tensionPAS={tensionPAS} setTensionPAS={setTensionPAS}
        tensionPAD={tensionPAD} setTensionPAD={setTensionPAD}
      />
    </Paper>

    {/* Entrevista final */}
    <Paper elevation={0} sx={{ ...sectionPaperSx, border: '1px solid #fcd34d', background: '#fffbeb' }}>
      <SectionTitle icon={<AssignmentIcon />}>Datos de la entrevista final</SectionTitle>
      <Divider sx={{ mb: 3, borderColor: '#fcd34d' }} />
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Caídas durante la participación"
            type="number"
            value={caidasFinales}
            onChange={(e) => setCaidasFinales(e.target.value)}
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Número de pastillas"
            type="number"
            value={numPastillas}
            onChange={(e) => setNumPastillas(e.target.value)}
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Número de analgésicos"
            type="number"
            value={numAnalgesicosFinal}
            onChange={(e) => setNumAnalgesicosFinal(e.target.value)}
            sx={fieldSx}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={<Checkbox checked={cambioMedicacionFinal} onChange={(e) => setCambioMedicacionFinal(e.target.checked)} sx={{ color: COLORS.primary, '&.Mui-checked': { color: COLORS.primary } }} />}
            label="Cambio de medicación"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={<Checkbox checked={consultaMedico} onChange={(e) => setConsultaMedico(e.target.checked)} sx={{ color: COLORS.primary, '&.Mui-checked': { color: COLORS.primary } }} />}
            label="Consulta médico (empeoramiento)"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={<Checkbox checked={ingresosHospital} onChange={(e) => setIngresosHospital(e.target.checked)} sx={{ color: COLORS.primary, '&.Mui-checked': { color: COLORS.primary } }} />}
            label="Ingresos en hospital"
          />
        </Grid>
      </Grid>
      <TextField
        fullWidth
        label="Detalles adicionales"
        multiline
        rows={3}
        value={datosInteresFinal}
        onChange={(e) => setDatosInteresFinal(e.target.value)}
        sx={fieldSx}
      />
    </Paper>
  </Stack>
);

// ── Main Component ────────────────────────────────────────────────────────────

const Entrevista = ({ idPaciente }) => {
  const navigate = useNavigate();
  const form = useEntrevistaForm({ idPaciente, navigate });

  const stepLabels = form.isFinalMode ? STEP_LABELS_FINAL : STEP_LABELS_FULL;

  const renderStep = () => {
    // In final mode: step 0 = Identificación, step 1 = Datos finales
    if (form.isFinalMode) {
      if (form.step === 0) {
        return (
          <StepIdentificacion
            pacienteId={form.pacienteId}
            tipoEntrevista={form.tipoEntrevista}
            setTipoEntrevista={form.setTipoEntrevista}
            stepErrors={form.stepErrors}
          />
        );
      }
      return (
        <StepDatosFinales
          tensionPMM={form.tensionPMM} setTensionPMM={form.setTensionPMM}
          tensionPAS={form.tensionPAS} setTensionPAS={form.setTensionPAS}
          tensionPAD={form.tensionPAD} setTensionPAD={form.setTensionPAD}
          caidasFinales={form.caidasFinales} setCaidasFinales={form.setCaidasFinales}
          numPastillas={form.numPastillas} setNumPastillas={form.setNumPastillas}
          numAnalgesicosFinal={form.numAnalgesicosFinal} setNumAnalgesicosFinal={form.setNumAnalgesicosFinal}
          cambioMedicacionFinal={form.cambioMedicacionFinal} setCambioMedicacionFinal={form.setCambioMedicacionFinal}
          consultaMedico={form.consultaMedico} setConsultaMedico={form.setConsultaMedico}
          ingresosHospital={form.ingresosHospital} setIngresosHospital={form.setIngresosHospital}
          datosInteresFinal={form.datosInteresFinal} setDatosInteresFinal={form.setDatosInteresFinal}
          expandarHistorialCompleto={form.expandarHistorialCompleto}
        />
      );
    }

    // Full mode (Inicial, or Final with expanded history)
    switch (form.step) {
      case 0:
        return (
          <StepIdentificacion
            pacienteId={form.pacienteId}
            tipoEntrevista={form.tipoEntrevista}
            setTipoEntrevista={form.setTipoEntrevista}
            stepErrors={form.stepErrors}
          />
        );
      case 1:
        return (
          <StepHabitos
            fumador={form.fumador} setFumador={form.setFumador}
            alcohol={form.alcohol} setAlcohol={form.setAlcohol}
          />
        );
      case 2:
        return (
          <StepAntecedentes
            caidas12Meses={form.caidas12Meses} setCaidas12Meses={form.setCaidas12Meses}
            numCaidas={form.numCaidas} setNumCaidas={form.setNumCaidas}
            ayudaLocomocion={form.ayudaLocomocion} setAyudaLocomocion={form.setAyudaLocomocion}
            protesisArticulares={form.protesisArticulares} setProtesisArticulares={form.setProtesisArticulares}
            protesis={form.protesis} setProtesis={form.setProtesis}
            vertigos={form.vertigos} setVertigos={form.setVertigos}
            mareosAndar={form.mareosAndar} setMareosAndar={form.setMareosAndar}
            cardiovascular={form.cardiovascular} setCardiovascular={form.setCardiovascular}
            cardioOptions={form.cardioOptions} setCardioOptions={form.setCardioOptions}
            respiratorio={form.respiratorio} setRespiratorio={form.setRespiratorio}
            respOptions={form.respOptions} setRespOptions={form.setRespOptions}
            genitoUrinario={form.genitoUrinario} setGenitoUrinario={form.setGenitoUrinario}
            genitoOptions={form.genitoOptions} setGenitoOptions={form.setGenitoOptions}
            endocrino={form.endocrino} setEndocrino={form.setEndocrino}
            endocrinoOptions={form.endocrinoOptions} setEndocrinoOptions={form.setEndocrinoOptions}
            digestivo={form.digestivo} setDigestivo={form.setDigestivo}
            digestOptions={form.digestOptions} setDigestOptions={form.setDigestOptions}
            musculoEsqueletico={form.musculoEsqueletico} setMusculoEsqueletico={form.setMusculoEsqueletico}
            musculoOptions={form.musculoOptions} setMusculoOptions={form.setMusculoOptions}
            nervioso={form.nervioso} setNervioso={form.setNervioso}
            nervOptions={form.nervOptions} setNervOptions={form.setNervOptions}
            sentidos={form.sentidos} setSentidos={form.setSentidos}
            sentidosOptions={form.sentidosOptions} setSentidosOptions={form.setSentidosOptions}
          />
        );
      case 3:
        return (
          <StepMedicacion
            medicacion={form.medicacion} setMedicacion={form.setMedicacion}
            medOptions={form.medOptions} setMedOptions={form.setMedOptions}
            numAnalgesicos={form.numAnalgesicos} setNumAnalgesicos={form.setNumAnalgesicos}
            tensionPMM={form.tensionPMM} setTensionPMM={form.setTensionPMM}
            tensionPAS={form.tensionPAS} setTensionPAS={form.setTensionPAS}
            tensionPAD={form.tensionPAD} setTensionPAD={form.setTensionPAD}
            datosInteres={form.datosInteres} setDatosInteres={form.setDatosInteres}
          />
        );
      case 4:
        return (
          <StepActividades
            actividades={form.actividades} setActividades={form.setActividades}
            tipoEntrevista={form.tipoEntrevista}
            caidasFinales={form.caidasFinales} setCaidasFinales={form.setCaidasFinales}
            numPastillas={form.numPastillas} setNumPastillas={form.setNumPastillas}
            numAnalgesicosFinal={form.numAnalgesicosFinal} setNumAnalgesicosFinal={form.setNumAnalgesicosFinal}
            cambioMedicacionFinal={form.cambioMedicacionFinal} setCambioMedicacionFinal={form.setCambioMedicacionFinal}
            consultaMedico={form.consultaMedico} setConsultaMedico={form.setConsultaMedico}
            ingresosHospital={form.ingresosHospital} setIngresosHospital={form.setIngresosHospital}
            datosInteresFinal={form.datosInteresFinal} setDatosInteresFinal={form.setDatosInteresFinal}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <Box sx={{ maxWidth: 860, width: '100%' }}>

        {/* Hero */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            mb: 3,
            borderRadius: 4,
            border: `1px solid ${COLORS.border}`,
            background: 'linear-gradient(135deg, #f8fbff 0%, #edf6ff 55%, #f5fbff 100%)',
            boxShadow: '0 20px 45px rgba(15, 23, 42, 0.08)',
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
            <Box>
              <Typography variant="overline" sx={{ color: COLORS.primaryDark, letterSpacing: 1.4 }}>
                Sistema de seguimiento clínico
              </Typography>
              <Typography variant="h4" sx={{ color: COLORS.textMain, fontWeight: 600, lineHeight: 1.1, mb: 1 }}>
                Entrevista clínica
              </Typography>
              <Typography sx={{ color: COLORS.textMuted }}>
                {stepLabels[form.step]} — paso {form.step + 1} de {form.TOTAL_STEPS}
              </Typography>
            </Box>
            <Chip
              label={form.tipoEntrevista || 'Sin tipo'}
              variant="outlined"
              sx={{ borderColor: '#bfdbfe', color: '#1e3a8a', fontWeight: 600 }}
            />
          </Stack>

          {/* Stepper */}
          <Box sx={{ mt: 3 }}>
            <Stepper activeStep={form.step} alternativeLabel>
              {stepLabels.map((label, index) => (
                <Step key={label} completed={index < form.step}>
                  <StepLabel
                    StepIconProps={{
                      sx: {
                        '&.Mui-active': { color: COLORS.primary },
                        '&.Mui-completed': { color: COLORS.primary },
                      },
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: form.step === index ? 700 : 400 }}>
                      {label}
                    </Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Paper>

        {/* Step content */}
        {renderStep()}

        {/* Navigation actions */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 4,
            border: `1px solid ${COLORS.border}`,
            backgroundColor: 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={form.step === 0 ? () => navigate('/home') : form.prevStep}
              sx={{ borderRadius: 2.5 }}
            >
              {form.step === 0 ? 'Cancelar' : 'Anterior'}
            </Button>

            <Typography variant="body2" sx={{ color: COLORS.labelMuted }}>
              {form.step + 1} / {form.TOTAL_STEPS}
            </Typography>

            {form.step < form.TOTAL_STEPS - 1 ? (
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={form.nextStep}
                sx={{ borderRadius: 2.5, px: 3, boxShadow: '0 8px 18px rgba(13,106,238,0.2)' }}
              >
                Siguiente
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={form.guardarDatos}
                disabled={form.isSaving}
                sx={{ borderRadius: 2.5, px: 3, boxShadow: '0 8px 18px rgba(13,106,238,0.2)' }}
              >
                {form.isSaving ? 'Guardando…' : 'Guardar entrevista'}
              </Button>
            )}
          </Stack>
        </Paper>

        <Snackbar open={form.openGood} autoHideDuration={3000} onClose={() => form.setOpenGood(false)}>
          <Alert severity="success" onClose={() => form.setOpenGood(false)}>
            Entrevista creada correctamente.
          </Alert>
        </Snackbar>
        <Snackbar open={form.openError} autoHideDuration={4000} onClose={() => form.setOpenError(false)}>
          <Alert severity="error" onClose={() => form.setOpenError(false)}>
            Error al guardar la entrevista. Inténtalo de nuevo.
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default Entrevista;
